import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Crown, ExternalLink, Search, History, Shield } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/**
 * /console/test-hq , Admin impersonation tool.
 *
 * Lets the admin mint a short-lived customer JWT for any account, then
 * opens Heritage HQ in a new tab with that token preloaded. Every
 * impersonation is audit-logged.
 *
 * The HQ page reads its token from localStorage key "heritage-customer-token";
 * we set that key + REDIRECT, no separate "switcher" UI on the customer side
 * is needed.
 */
export default function ConsoleTestHq() {
  const [recent, setRecent] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get("/console/test-hq/recent-customers", { params: { limit: 50 } }),
          api.get("/console/test-hq/impersonate-log", { params: { limit: 50 } }),
        ]);
        if (cancelled) return;
        setRecent(Array.isArray(r1.data) ? r1.data : []);
        setLog(Array.isArray(r2.data) ? r2.data : []);
      } catch {
        if (!cancelled) toast.error("Couldn't load Test HQ.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const impersonate = async (email) => {
    if (!email) return;
    setBusy(true);
    try {
      const r = await api.post("/console/test-hq/impersonate", { email });
      // Preserve the admin's real customer token (if any) so we can restore it after.
      const realCustomerToken = window.localStorage.getItem("heritage-customer-token");
      if (realCustomerToken) {
        window.localStorage.setItem("heritage-customer-token-restore", realCustomerToken);
      }
      window.localStorage.setItem("heritage-customer-token", r.data.token);
      window.localStorage.setItem("heritage-impersonation", JSON.stringify({
        target_email: r.data.email,
        target_name: r.data.name,
        impersonator: "admin",
        opened_at: new Date().toISOString(),
      }));
      toast.success(`Impersonating ${r.data.name || r.data.email}. Opening Heritage HQ…`);
      window.open("/account", "_blank", "noopener");
      // Refresh the log.
      const lr = await api.get("/console/test-hq/impersonate-log", { params: { limit: 50 } });
      setLog(Array.isArray(lr.data) ? lr.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start impersonation.");
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return recent;
    return recent.filter((c) =>
      (c.email || "").toLowerCase().includes(needle) ||
      (c.name || "").toLowerCase().includes(needle),
    );
  }, [recent, q]);

  return (
    <div data-testid="console-test-hq">
      <header className="mb-5">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">Internal</div>
        <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1 flex items-center gap-3">
          <Shield className="w-7 h-7 text-[#6E1E3A]" /> Test HQ
        </h1>
        <p className="text-[#7A6A55] mt-2 max-w-2xl text-sm">
          Open Heritage HQ as any customer to walk through their flows. Every impersonation is audit-logged. The customer&apos;s password is never used or revealed.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Quick pick + manual entry */}
        <section className="bg-white border border-[#E8DFCB] rounded p-5 space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold mb-1.5">By email</div>
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="customer@example.com"
                data-testid="test-hq-email-input"
                className="flex-1 px-3 py-2 bg-[#FBF6E9] border border-[#E8DFCB] rounded text-sm outline-none focus:border-[#6E1E3A]"
              />
              <button
                type="button" disabled={busy || !emailInput.trim()}
                onClick={() => impersonate(emailInput.trim().toLowerCase())}
                data-testid="test-hq-go"
                className="inline-flex items-center gap-1.5 h-10 px-4 bg-[#6E1E3A] hover:bg-[#5a1830] text-white text-[10px] tracking-[0.25em] uppercase rounded disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />} Open as customer
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold">Quick pick · {filtered.length} customers</div>
              <div className="flex items-center gap-1.5 bg-[#FBF6E9] border border-[#E8DFCB] rounded px-2 py-1">
                <Search className="w-3 h-3 text-[#9C8B73]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" data-testid="test-hq-search" className="outline-none text-xs bg-transparent w-32" />
              </div>
            </div>
            {loading ? (
              <div className="p-6 grid place-items-center text-[#9C8B73]"><Loader2 className="w-4 h-4 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#9C8B73]">No customers match.</div>
            ) : (
              <div className="border border-[#E8DFCB] rounded divide-y divide-[#F4ECD8] max-h-[420px] overflow-auto">
                {filtered.map((c) => (
                  <button
                    key={c.email}
                    type="button"
                    onClick={() => impersonate(c.email)}
                    disabled={busy}
                    data-testid={`test-hq-pick-${c.email}`}
                    className="w-full p-3 text-left grid grid-cols-12 gap-2 items-center hover:bg-[#FBF6E9] transition"
                  >
                    <div className="col-span-6 md:col-span-5">
                      <div className="font-serif text-sm text-[#3C2A1E]">{c.name || "-"}</div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] truncate">{c.email}</div>
                    </div>
                    <div className="col-span-3 md:col-span-3 text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">
                      {c.vip_box_enabled ? "VIP " : ""}{c.vip_mystery_enabled ? "Mystery " : ""}
                      {Number(c.heritage_credits || 0) > 0 ? `· NZ$${Number(c.heritage_credits).toFixed(0)}` : ""}
                    </div>
                    <div className="col-span-3 md:col-span-3 text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] text-right">
                      {c.from_public_mystery && <span className="px-1 py-0.5 bg-[#C49247] text-white rounded mr-1">Public</span>}
                      <Crown className="inline w-3 h-3 opacity-60" />
                    </div>
                    <div className="col-span-12 md:col-span-1 text-right">
                      <ExternalLink className="w-3 h-3 text-[#6E1E3A] inline" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Audit log */}
        <aside className="bg-white border border-[#E8DFCB] rounded overflow-hidden flex flex-col">
          <header className="px-4 py-3 bg-[#FBF6E9] border-b border-[#E8DFCB]">
            <div className="font-display text-lg text-[#3C2A1E] flex items-center gap-2">
              <History className="w-4 h-4 text-[#6E1E3A]" /> Impersonation log
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] mt-0.5">Most recent 50 sessions</div>
          </header>
          <div className="flex-1 overflow-auto divide-y divide-[#F4ECD8] max-h-[560px]" data-testid="test-hq-log">
            {log.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#9C8B73]">No impersonation activity yet.</div>
            ) : log.map((entry) => (
              <div key={entry.id} className="p-3 text-xs">
                <div className="font-serif text-sm text-[#3C2A1E]">{entry.target_name || entry.target_email}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] mt-0.5">{entry.target_email}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] mt-0.5">
                  by {entry.actor} · {new Date(entry.created_at).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
